import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole, isAdminRole } from '../../utils/roleUtils';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV } from '../../utils/commonHandlers';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import Button from '../../composants/Button/Button';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';

import './Operation.css';

interface Operation {
  id: string;
  numeroDeOperation?: string;
  creePar: string;
  titre: string;
  client: string;
  prestataire: string;
  montant: number;
  status: string | {
    id: number;
    action: boolean;
    nom: string;
    delai: number | null;
    type_compte: string;
  };
  rawApiStatus?: string;
  type: 'BC' | 'MS';
  dateCreation: string;
  lotTechnique: string;
  lotGeographique: string;
  noteTotale?: number;
  noteTechnique?: number;
  noteFinanciere?: number;
  commentaire?: string;
  delais?: number;
  expire_at?: string;
  visite?: boolean;
  reponseTechnique?: string;
  idContratSub?: string | number;
  actionRequise?: boolean;
  // Nouveau champ pour accéder facilement à l'ID du statut
  statusId?: number;
}

interface FilterOptions {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  lotTechnique: string;
  lotGeographique: string;
  createur: string;
  nomOperation: string;
  client: string;
  prixRange: {
    min: number;
    max: number;
  };
  status: string;
  type: string;
}

function Operation({ onNotificationUpdate }: { onNotificationUpdate?: (count: number) => void }) {
  const { user } = useAuth();
  // Vue client
  const isClientView = isClientRole(user?.role || '');

  // Vu admin
  // const isAdmin = isAdminRole(user?.role || '');

  const navigate = useNavigate();

  // Modal de lancement marché subséquent
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(false);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(false);


  // Handler pour ouvrir le modal de lancement de marché subséquent
  // Helper pour récupérer le rôle utilisateur du localStorage
  const getUserRoleFromLocalStorage = (): string => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        return userData.role || '';
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du rôle utilisateur:', error);
    }
    return '';
  };

  // Fonction qui détermine quelle action afficher en fonction du statut et du rôle de l'utilisateur
  const getStatusActionMapping = (statusId: number, userRole: string, isActionRequired: boolean = false) => {
    // Mapping complet des statuts vers leurs actions
    const statusActionMap: {
      [key: number]: {
        buttonText: string,
        action: string,
        forRoles: string[],
        requiresAction: boolean
      }
    } = {
      1: { // Brouillon
        buttonText: 'À reprendre',
        action: 'reprendre',
        forRoles: ['Admin', 'Acheteur', 'Acheteur admin'],
        requiresAction: false
      },
      2: { // Candidature en cours
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      3: { // A candidater
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      4: { // En analyse
        buttonText: 'À analyser',
        action: 'analyser',
        forRoles: ['Acheteur', 'Acheteur admin', 'Admin'],
        requiresAction: true
      },
      5: { // En attente de l'analyse
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: true
      },
      6: { // Refuser
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      7: { // En attente de votre signature (acheteur)
        buttonText: 'À signer',
        action: 'signer',
        forRoles: ['Acheteur signataire', 'Acheteur signataire admin', 'Admin'],
        requiresAction: true
      },
      8: { // En attente decision
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      9: { // En attente de signature prestataire
        buttonText: 'Relancer',
        action: 'relancer',
        forRoles: ['Acheteur signataire', 'Acheteur signataire admin', 'Admin'],
        requiresAction: false
      },
      10: { // En attente de votre signature (prestataire)
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      },
      11: { // Valider (Accepté)
        buttonText: 'PDF',
        action: 'export',
        forRoles: ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin', 'Acheteur admin', 'Admin'],
        requiresAction: false
      },
      12: { // Expirer
        buttonText: '',
        action: '',
        forRoles: [],
        requiresAction: false
      }
    };

    // Si le statut n'existe pas dans le mapping, retourner une configuration par défaut
    if (!statusActionMap[statusId]) {
      return {
        buttonText: '',
        action: '',
        showButton: false,
        disableButton: true
      };
    }

    const mapping = statusActionMap[statusId];

    // Déterminer si le bouton doit être affiché en fonction du rôle de l'utilisateur
    // Vérifier si le rôle de l'utilisateur (même partiel) correspond à l'un des rôles autorisés
    const showButton = mapping.buttonText !== '' &&
      (mapping.forRoles.some(role =>
        userRole.toLowerCase().includes(role.toLowerCase())) ||
        mapping.forRoles.includes('*'));

    // Déterminer si le bouton doit être activé en fonction de requiresAction
    // Si requiresAction est true, le bouton ne doit être activé que si action est true dans le statut
    // Pour le statut Brouillon (ID 1), on affiche toujours le bouton indépendamment de l'action
    const disableButton = statusId !== 1 && mapping.requiresAction && !isActionRequired;

    return {
      buttonText: mapping.buttonText,
      action: mapping.action,
      showButton,
      disableButton
    };
  };

  const handleOpenLancerMarcheModal = () => {
    setIsLancerMarcheModalOpen(true);
  };

  // Définir les onglets
  const tabs = [
    'Tous',
    'Action requise',
    'En attente',
    'Brouillons',
    'Accepté',
    'Refusé / Expiré' // ✅ Fusionné
  ];

  // Mapping entre les onglets et les IDs de statut de l'API
  const tabToStatusMapping: { [key: number]: { filter: string, statusIds: number[] } } = {
    0: { filter: '', statusIds: [] }, // Tous
    1: { filter: 'status_action=1', statusIds: [] }, // Action requise - filtre par l'attribut action=true
    2: { filter: 'status_type_compte=Acheteur&&status_action=0', statusIds: [] }, // En attente
    3: { filter: 'status_id=1', statusIds: [1] }, // Brouillons - statut 1 (Brouillon)
    4: { filter: 'status_id=11', statusIds: [11] }, // Accepté - statut 11 (Valider)
    5: { filter: 'status_id=6,12', statusIds: [6, 12] }, // ✅ Fusionné
  };

  // Définir l'onglet actif par défaut
  const getInitialActiveTab = () => {
    return tabs.indexOf('Action requise');
  };

  // Initialiser activeTab avec la valeur appropriée
  const [activeTab, setActiveTab] = useState(() => getInitialActiveTab());

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1); // Page en cours
  const itemsPerPage = 50; // Nombre d'éléments par page

  // Pour les notifications de operations
  const [unreadOperations, setUnreadLocalOperations] = useState<number>(0);


  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [filteredData, setFilteredData] = useState<Operation[]>([]);
  const { setUnreadOperations } = useNotifications();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [operationToRefuse, setOperationToRefuse] = useState<string | null>(null);

  const typeOptions = [
    { label: 'Tous', value: 'Tous' },
    { label: 'Bon de Commande', value: 'BC' },
    { label: 'Marché Subséquent', value: 'MS' }
  ];

  const [selectedType, setSelectedType] = useState<string>(typeOptions[0].value);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 50,
    total: 0
  });

  // Pour le changement d'onglet
  const handleTabChange = (tabIndex: number) => {
    console.log("Changement d'onglet vers:", tabIndex, "Nom de l'onglet:", tabs[tabIndex]);
    setActiveTab(tabIndex);
    // Recharger les données avec le nouvel onglet sélectionné
    fetchOperations(1, tabIndex, selectedType);
  };

  // Pour le changement de type
  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    // Recharger les données avec le nouveau type sélectionné
    fetchOperations(1, activeTab, value);
  };

  // Fonctions de navigation
  const handleViewDetails = (id: string) => {
    navigate(`/operations/${id}`);
  };

  const handleCandidater = (operationId: string) => {
    navigate(`/operations/${operationId}?tab=1`, { state: { action: 'candidater' } });
  };

  const handleAnalyser = (operationId: string) => {
    navigate(`/operations/${operationId}?tab=2`, { state: { action: 'analyser' } });
  };

  const handleReprendre = (operationId: string) => {
    navigate(`/operations/${operationId}?tab=1`, { state: { action: 'reprendre' } });
  };

  const handleSigner = (operationId: string) => {
    navigate(`/operations/${operationId}?tab=3`, { state: { action: 'signer' } });
  };

  // Quand on clique sur Refuser
  const handleRefuserClick = (id: string) => {
    setOperationToRefuse(id);
    setIsRefuseModalOpen(true);
  };

  // Quand on confirme dans la popup
  const handleConfirmRefuse = async () => {
    if (operationToRefuse) {
      try {
        const token = localStorage.getItem('token');

        if (!token) {
          throw new Error('Aucun token d\'authentification trouvé');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${operationToRefuse}/refuser`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du refus de l\'opération');
        }

        // Mettre à jour l'interface utilisateur après le refus
        setOperations(prev =>
          prev.map(cmd =>
            cmd.id === operationToRefuse ? { ...cmd, status: "Refusé" } : cmd
          )
        );
        setFilteredData(prev =>
          prev.map(cmd =>
            cmd.id === operationToRefuse ? { ...cmd, status: "Refusé" } : cmd
          )
        );

        // Optionnellement, rafraîchir complètement les données
        // fetchOperations(currentPage, activeTab, selectedType);
      } catch (error) {
        console.error('Erreur lors du refus:', error);
        alert('Erreur lors du refus de l\'opération.');
      }
    }
    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  // Quand on annule dans la popup
  const handleCancelRefuse = () => {
    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = async (query: string) => {
    setSearchLoading(true);
    try {
      // Si la requête est vide, afficher toutes les operations
      if (!query.trim()) {
        await fetchOperations(1, activeTab, selectedType);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation?operation_nom=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }

      const data = await response.json();

      if (data.status === 'success') {
        // Formater les données reçues
        const formattedData = await Promise.all(formatApiDataToOperations(data.data));

        // Mettre à jour les données et la pagination
        setOperations(formattedData);
        setFilteredData(formattedData);
        setPagination({
          currentPage: 1,
          lastPage: Math.ceil(formattedData.length / itemsPerPage),
          perPage: itemsPerPage,
          total: formattedData.length
        });
      } else {
        setOperations([]);
        setFilteredData([]);
        setPagination({
          currentPage: 1,
          lastPage: 1,
          perPage: itemsPerPage,
          total: 0
        });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setError('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Mise à jour des compteurs de notifications
  const getActionRequiseCount = () => {
    if (!operations || !Array.isArray(operations)) {
      return 0;
    }

    return operations.filter(cmd => cmd && cmd.actionRequise === true).length;
  };

  // Create notifications array for TabNav: notification only for "Action requise" tab
  const notifications = [
    {
      tabIndex: tabs.indexOf('Action requise'),
      count: getActionRequiseCount()
    }
  ].filter(n => n.tabIndex !== -1 && n.count > 0);

  // Update the notification count whenever operations changes
  useEffect(() => {
    const count = getActionRequiseCount();

    setUnreadOperations(count);
    setUnreadLocalOperations(count);
    onNotificationUpdate?.(count);
  }, [operations, setUnreadOperations, onNotificationUpdate]);

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleRowClick = (id: string) => {
    const operation = operations.find(op => op.id === id);
    if (!operation) return;

    // Utiliser les statuts bruts pour la navigation car l'interface est complexe
    let action = '';
    let tab = 0;

    // Récupérer le statut ID et déterminer si une action est requise
    let statusId = 0;

    // Tenter de récupérer l'ID du statut s'il existe
    try {
      if (operation.status && typeof operation.status === 'object') {
        const statusObj = operation.status as any;
        if (statusObj.id && typeof statusObj.id === 'number') {
          statusId = statusObj.id;
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du statusId:', error);
    }

    const isActionRequired = operation.actionRequise === true;
    const userRole = getUserRoleFromLocalStorage();

    // Utiliser la fonction de mapping pour déterminer l'action à effectuer
    if (statusId) {
      const actionMapping = getStatusActionMapping(statusId, userRole, isActionRequired);

      // Si un bouton doit être affiché et n'est pas désactivé, utiliser son action
      if (actionMapping.showButton && !actionMapping.disableButton) {
        action = actionMapping.action;

        // Déterminer l'onglet en fonction de l'action
        switch (action) {
          case 'reprendre':
            tab = 1; // Tab Consultation
            break;
          case 'analyser':
            tab = 2; // Tab Analyse
            break;
          case 'signer':
            tab = 3; // Tab Les pièces du marché
            break;
          case 'candidater':
            tab = 1; // Tab Candidature
            break;

          default:
            tab = 0; // Tab par défaut
        }

        navigate(`/operations/${id}?tab=${tab}`, { state: { action } });
        return;
      }
      else {
        let statusName = '';

        if (typeof operation.status === 'object' && operation.status !== null) {
          statusName = operation.status.nom;
        } else if (typeof operation.status === 'string') {
          statusName = operation.status;
        }

        switch (statusName) {
          case 'Candidature en cours':
            tab = 2;
            break;
          case 'En attente de signature prestataire':
            tab = 3;
            break;
          default:
            tab = 0;
        }

        navigate(`/operations/${id}?tab=${tab}`, { state: { action } });
        return;
      }

    }

    // console.log(operation)
    // // // Logique de secours basée sur le nom du statut brut
    // if (operation.rawApiStatus === 'Brouillon') {
    //   action = 'reprendre';
    //   tab = 1; // Tab Consultation
    // } else if (operation.rawApiStatus === 'En attente de l\'analyse' || operation.rawApiStatus === 'Analyser') {
    //   action = 'analyser';
    //   tab = 2; // Tab Analyse
    // } else if (operation.rawApiStatus === 'En attente de votre signature' || operation.rawApiStatus === 'Signer' || operation.rawApiStatus === 'En attente de signature prestataire') {
    //   action = 'signer';
    //   tab = 3; // Tab Les pièces du marché
    // } else if (operation.rawApiStatus === 'Candidater') {
    //   action = 'candidater';
    //   tab = 1; // Tab Candidature pour prestataire
    // } else {
    //   action = 'view';
    //   tab = 0; // Tab par défaut
    // }

    // navigate(`/operations/${id}?tab=${tab}`, { state: { action } });
  };

  const handleExport = (id: string) => {
    const operationToExport = operations.find(cmd => cmd.id === id);

    if (operationToExport) {
      handleExportCSV([operationToExport], columns, `Operation-${id}-${new Date().toISOString().slice(0, 10)}.csv`);
    }
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operations/${id}/relancer-signataire`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la relance');
      }

      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  const handlePageChange = (page: number) => {
    // console.log("Changement de page vers:", page);
    setCurrentPage(page);
    fetchOperations(page, activeTab, selectedType);
  };

  const getApiStatusFromUiStatus = (uiStatus: string): string | null => {
    switch (uiStatus) {
      case 'Brouillons':
        return 'status_id=1';

      case 'Accepté':
        return 'status_id=11';

      case 'Refusé':
        return 'status_id=6';

      case 'Expiré':
        return 'status_id=12';

      case 'Action requise':
        return 'status_action=1';

      case 'En attente':
        return 'status_nom="En attente"&&status_action=0';

      default:
        return null;
    }
  };

  const columns = [
    {
      field: 'numeroDeOperation',
      header: 'N°',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'type',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'dateCreation',
      header: 'Date',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'lotTechnique',
      header: 'Lot Technique',
      renderCell: (value: string) => (
        <div className="lot-technique text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'lotGeographique',
      header: 'Dép',
      renderCell: (value: string) => (
        <div className="lot-geographique text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'creePar',
      header: 'Créé par',
      renderCell: (value: string) => (
        <div className="user-info text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: 'titre',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="operation-nom text-truncate" title={value || '-'}
        >
          {value || '-'}
        </div>
      )
    },
    {
      field: isClientView ? 'prestataire' : 'client',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: string) => (
        <div className="client-info text-truncate" title={value ? `${value}` : '-'}
        >
          {value ? `${value}` : '-'}
        </div>
      )
    },
    {
      field: 'montant',
      header: 'Total HT',
      renderCell: (value: number) => (
        <div className="montant-operation">
          {value !== undefined && value !== null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: (value: string | { id: number; action: boolean; nom: string; delai: number | null; type_compte: string; }, row: Operation) => {
        if (!row) return null;

        // Extraire le nom du statut, qu'il soit une chaîne ou un objet
        let statusName = '';
        let isActionRequired = false;

        if (typeof value === 'string') {
          statusName = value;
          isActionRequired = row.actionRequise === true;
        } else if (typeof value === 'object' && value !== null) {
          statusName = value.nom || '';
          isActionRequired = value.action === true;
        } else {
          statusName = 'inconnu';
        }

        // Transformer le statut en classe CSS
        const statusClass = statusName.toLowerCase().replace(/[^a-z0-9]/g, '-');

        // Ajouter la classe "action-requise" si une action est requise
        const actionClass = isActionRequired ? 'action-requise' : '';

        return (
          <div className={`status-badge ${statusClass} ${actionClass}`}>
            {statusName}
          </div>
        );
      }
    },
    {
      field: 'tempsRestant',
      header: 'Temps restant',
      renderCell: (value: string, row: any) => {
        if (!row) return null;

        // Afficher le compte à rebours pour tous les statuts qui ont un délai
        if (row.expire_at) {
          return <CompteARebourd targetDate={row.expire_at} />;
        }
        return null;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: string, row: Operation) => {
        if (!row) return null;

        // Récupérer le statut ID directement depuis statusId ou depuis l'objet status
        let statusId = 0;
        let isActionRequired = false;

        // Utiliser le statusId s'il existe, sinon essayer de l'extraire de status
        if (typeof row.statusId === 'number') {
          statusId = row.statusId;
        } else if (typeof row.status === 'object' && row.status !== null && 'id' in row.status) {
          statusId = (row.status as any).id || 0;
        }

        // Déterminer si une action est requise
        if (typeof row.actionRequise === 'boolean') {
          isActionRequired = row.actionRequise;
        } else if (typeof row.status === 'object' && row.status !== null && 'action' in row.status) {
          isActionRequired = (row.status as any).action === true;
        }

        // Récupérer le rôle utilisateur du localStorage
        const userRole = getUserRoleFromLocalStorage();

        // Utiliser notre fonction de mapping pour déterminer l'action à afficher
        const actionMapping = getStatusActionMapping(statusId, userRole, isActionRequired);

        // Ne pas afficher de bouton si showButton est false
        if (!actionMapping.showButton) {
          return null;
        }

        // Déterminer le nom du statut pour la classe CSS
        let statusName = '';
        if (typeof row.status === 'string') {
          statusName = row.status;
        } else if (typeof row.status === 'object' && row.status !== null && 'nom' in row.status) {
          statusName = (row.status as any).nom || '';
        } else if (row.rawApiStatus) {
          statusName = row.rawApiStatus;
        }

        // Transformer le nom du statut en classe CSS
        const statusClass = statusName.toLowerCase().replace(/[^a-z0-9]/g, '-');

        return (
          <Button
            value={actionMapping.buttonText}
            variant="secondary"
            size="small"
            onClick={() => {
              // Exécuter l'action en fonction du mapping
              switch (actionMapping.action) {
                case 'reprendre':
                  handleReprendre(row.id);
                  break;
                case 'analyser':
                  handleAnalyser(row.id);
                  break;
                case 'candidater':
                  handleCandidater(row.id);
                  break;
                case 'signer':
                  handleSigner(row.id);
                  break;
                case 'export':
                  handleExport(row.id);
                  break;
                case 'relancer':
                  handleRelancerSignataire(row.id);
                  break;
                default:
                  // Action par défaut ou pas d'action
                  console.log(`Aucune action définie pour le statut ${statusId}`);
              }
            }}
            className={`action-button ${statusClass} ${isActionRequired ? 'action-requise' : ''}`}
            disabled={actionMapping.disableButton}
          />
        );
      }
    }
  ];

  // Add maxPrice calculation
  const maxPrice = useMemo(() =>
    Math.max(...operations.map(cmd => cmd.montant || 0))
    , [operations]);

  const handleFilterApply = (filters: FilterOptions) => {
    let filtered = [...operations];

    // Apply date range filter
    if (filters.dateRange.start && filters.dateRange.end) {
      filtered = filtered.filter(cmd => {
        const cmdDate = new Date(cmd.dateCreation.split('/').reverse().join('-'));
        const startDate = filters.dateRange.start as Date;
        const endDate = filters.dateRange.end as Date;
        return cmdDate >= startDate && cmdDate <= endDate;
      });
    }

    // Apply lot technique filter
    if (filters.lotTechnique) {
      filtered = filtered.filter(cmd => cmd.lotTechnique === filters.lotTechnique);
    }

    // Apply lot géographique filter
    if (filters.lotGeographique) {
      filtered = filtered.filter(cmd => cmd.lotGeographique === filters.lotGeographique);
    }

    // Apply créateur filter
    if (filters.createur) {
      filtered = filtered.filter(cmd => cmd.creePar === filters.createur);
    }

    // Apply nom de Operation filter
    if (filters.nomOperation) {
      filtered = filtered.filter(cmd =>
        cmd.titre.toLowerCase().includes(filters.nomOperation.toLowerCase())
      );
    }

    // Apply client filter
    if (filters.client) {
      filtered = filtered.filter(cmd => cmd.client === filters.client);
    }

    // Apply price range filter with maxPrice
    if (filters.prixRange.min > 0 || filters.prixRange.max < maxPrice) {
      filtered = filtered.filter(cmd =>
        cmd.montant >= filters.prixRange.min && cmd.montant <= filters.prixRange.max
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(cmd => cmd.status === filters.status);
    }

    // Apply type filter
    if (filters.type && filters.type !== 'Tous') {
      filtered = filtered.filter(cmd => cmd.type === filters.type);
    }

    // Update filtered data and close modal
    setFilteredData(filtered);
    setIsFilterModalOpen(false);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  };

  const formatApiDate = (apiDate: string): string => {
    if (!apiDate) return '';
    try {
      const date = new Date(apiDate);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return '';
    }
  };

  const formatApiDataToOperations = (apiData: any[]): Promise<Operation>[] => {
    return apiData.map(async (operation: any, index: number) => {
      // Récupérer et formater le nom du créateur (C. Martin)
      const formatCreePar = (fullName: string): string => {
        if (!fullName) return 'Inconnu';
        const parts = fullName.trim().split(' ');
        if (parts.length < 2) return fullName;

        // Récupérer l'initiale du prénom et le nom
        const prenom = parts[0];
        const nom = parts.slice(1).join(' ');
        return `${prenom.charAt(0)}. ${nom}`;
      };

      // Récupérer directement le statut et l'action depuis l'API
      const apiStatus = operation.status || {};
      const rawApiStatus = apiStatus.nom || 'inconnu';

      // Déterminer si une action est requise en fonction de la propriété action du statut
      const actionRequise = apiStatus.action === true;

      // Simplifier la logique des statuts en utilisant directement celui de l'API
      let status = rawApiStatus;

      // Stocker l'objet de statut complet pour conserver l'ID et d'autres propriétés
      let statusObject = null;

      try {
        if (typeof apiStatus === 'object' && apiStatus !== null) {
          statusObject = {
            id: apiStatus.id || 0,
            action: apiStatus.action || false,
            nom: apiStatus.nom || 'inconnu',
            delai: apiStatus.delai || null,
            type_compte: apiStatus.type_compte || ''
          };
        }
      } catch (error) {
        console.error('Erreur lors de la création de l\'objet de statut:', error);
      }

      // Quelques ajustements pour la cohérence d'affichage dans l'UI
      if (rawApiStatus === 'Brouillon') status = 'Brouillons';
      else if (actionRequise) status = 'Action requise';

      // Déterminer le type d'opération (BC ou MS)
      const operationType = operation.type === 'BC' ? 'BC' : 'MS' as ('BC' | 'MS');

      return {
        id: operation.id?.toString() || '',
        numeroDeOperation: operation.numero || `MS-${operation.id}` || '',
        creePar: operation.organisme_nom || 'Inconnu',
        titre: operation.nom || `Operation sans titre ${index}`,
        client: operation.organisme_nom || 'Client inconnu',
        prestataire: operation.prestataire?.entite?.nom || '-',
        montant: operation.prix_total ? parseFloat(operation.prix_total) : 0,
        status: statusObject || status, // Utiliser l'objet de statut complet ou la chaîne de statut
        rawApiStatus: rawApiStatus,
        type: operationType,
        dateCreation: operation.created_at ? formatApiDate(operation.created_at) : new Date().toLocaleDateString('fr-FR'),
        lotTechnique: operation.lot_technique?.id
          ? `${operation.lot_technique.id} - ${operation.lot_technique.nom}`
          : operation.lot_technique?.nom || 'Non spécifié',
        lotGeographique: operation.id_lot_departement?.toString() || 'Non spécifié',
        noteTotale: operation.note_total,
        noteTechnique: operation.id_note_technique,
        noteFinanciere: operation.note_financiere,
        commentaire: operation.commentaire,
        delais: apiStatus.delai,
        expire_at: operation.expire_at,
        visite: operation.visite,
        reponseTechnique: operation.reponse_technique,
        idContratSub: operation.id_lot_technique,
        actionRequise: actionRequise,
        // Ajouter l'ID de statut directement pour faciliter l'accès
        statusId: statusObject?.id || 0
      };
    });
  };

  const fetchOperations = async (page = 1, tab = activeTab, type = selectedType) => {
    setLoading(true);
    setError(null);
    // console.log("Démarrage de fetchOperations pour la page", page);

    try {
      const token = localStorage.getItem('token');
      // console.log("Token trouvé:", token ? "Oui" : "Non");

      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      // Construction des paramètres de requête
      let queryParams = `page=${page}&per_page=100`;

      // Ajout du filtre par statut selon l'onglet actif
      if (tab !== 0 && tab >= 0 && tab < tabs.length) {
        const tabMapping = tabToStatusMapping[tab];
        if (tabMapping && tabMapping.filter) {
          queryParams = `${tabMapping.filter}&page=${page}&per_page=100`;
          console.log(`Filtrage par onglet: ${tabs[tab]} => filtre API: "${tabMapping.filter}"`);
        }
      }

      // Ajout du filtre par type
      if (type !== 'Tous') {
        queryParams += `&type=${type}`;
      }

      // Effectuer la requête API avec les filtres appliqués
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log("Statut de la réponse HTTP:", response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Erreur lors de la récupération des opérations: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log("Réponse API reçue:", responseData);

      // Vérifier que la réponse a le bon format
      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        console.error("Format de réponse incorrect:", responseData);
        throw new Error('Format de réponse API inattendu');
      }

      // Stocker les informations de pagination
      if (responseData.pagination) {
        console.log("Pagination trouvée directement dans responseData:", responseData.pagination);
        setPagination({
          currentPage: responseData.pagination.current_page || 1,
          lastPage: responseData.pagination.last_page || 1,
          perPage: responseData.pagination.per_page || 50,
          total: responseData.pagination.total || 0
        });
      }
      // Ancien format (dans data.pagination)
      else if (responseData.data && responseData.data.pagination) {
        console.log("Pagination trouvée dans responseData.data:", responseData.data.pagination);
        setPagination({
          currentPage: responseData.data.pagination.current_page || 1,
          lastPage: responseData.data.pagination.last_page || 1,
          perPage: responseData.data.pagination.per_page || 50,
          total: responseData.data.pagination.total || 0
        });
      }
      else {
        console.log("Aucune information de pagination trouvée, utilisation de valeurs par défaut");

        // Utiliser la taille du tableau pour estimer le nombre de pages
        const itemCount = Array.isArray(responseData.data) ? responseData.data.length : 0;
        const totalItems = Math.max(itemCount, page * 50); // Au moins assez pour la page actuelle

        setPagination({
          currentPage: page,
          lastPage: Math.ceil(totalItems / 50),
          perPage: 50,
          total: totalItems
        });

        console.log(`Pagination estimée: page ${page}, items: ${itemCount}, total estimé: ${totalItems}`);
      }

      console.log("Nombre d'opérations reçues:", responseData.data.length);

      // Transformer les données de l'API en format compatible avec notre interface Operation
      const operationPromises = formatApiDataToOperations(responseData.data);
      const formattedData = await Promise.all(operationPromises);

      // Mise à jour des données
      setFilteredData(formattedData);
      setOperations(formattedData);

      // Mise à jour des notifications
      updateNotifications(formattedData);

    } catch (err: any) {
      console.error("=== ERREUR DE CHARGEMENT ===");
      console.error("Message d'erreur:", err.message);
      console.error("Objet d'erreur complet:", err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'exportation des opérations');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-operations-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des opérations');
    } finally {
      setIsExporting(false);
    }
  }

  const updateNotifications = (operations: Operation[]) => {
    try {
      const count = operations.filter(cmd => cmd.actionRequise).length;
      setUnreadLocalOperations(count);

      // Utiliser la fonction setter du contexte si disponible
      if (typeof setUnreadOperations === 'function') {
        setUnreadOperations(count);
      }

      // Appel du callback de mise à jour fourni par le parent, si disponible
      if (typeof onNotificationUpdate === 'function') {
        onNotificationUpdate(count);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notifications:", error);
      // Ne pas bloquer l'exécution en cas d'erreur dans les notifications
    }
  };

  // <--- AJOUT : Charger les operations dès le montage
  useEffect(() => {
    fetchOperations(currentPage, activeTab, selectedType);
  }, []);

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <h1>Toutes les opérations</h1>
        <div className="button-group">
          <div className='button-filter-group'>
            <SearchBar
              onSearch={handleSearch}
              placeholder="Trouver une opération"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="third"
              size="small"
              icon={<ExportIcon />}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <Button
              value="filtrer"
              variant="third"
              size="small"
              icon={<FilterIcon />}
              onClick={handleFilter}
            />
          </div>
          <div className="commandes-actions">
            <Button
              value="Nouveau Bon de Commande"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsBonCommandeModalOpen(true)}
            />
            <Button
              value="Nouveau Marché Subséquent"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={handleOpenLancerMarcheModal}
            />
          </div>
        </div>
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
        />
        {loading ? (
          <div className="loading-indicator">
            <p>Chargement des opérations en cours...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <TableauDeBord
            data={filteredData}
            columns={columns as any}
            onSearch={handleSearch}
            typeFilters={true}
            onExport={() => handleExportCSV(filteredData, columns as any, `Operations-${new Date().toISOString().slice(0, 10)}.csv`)}
            onRowClick={handleRowClick}
            isSelectable={true}
            showActionBar={true}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
            showActionsColumn={false}
            itemsPerPage={pagination.perPage}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
          />
        )}
      </section>
      {/* Pour Marché Subséquent (usage actuel) */}
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
      />

      {/* Pour Bon de Commande (nouveau usage) */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={false}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
      />
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={operations}
        maxPrice={maxPrice}
      />
    </div>
  );
}

export default Operation;